import { useCallback, useEffect, useMemo, useState } from "react";
import { Chain } from "@swapkit/core";
import { AnimatePresence, LayoutGroup } from "framer-motion";
import { ListTodo } from "lucide-react";

import { ForgeHistoryLog } from "@coinbot/forge-api/trpc";

import { TransactionHistoryRow } from "~/components/complex/TransactionsHistory";
import { Button, Popover, PopoverClose, PopoverContent, PopoverTrigger } from "~/components/ui";
import { useSwapKit } from "~/context/swapkit";
import { api } from "~/services/api";

export function TransactionHistory() {
  const hiddenTransactions: string[] = localStorage.getItem("hiddenTransactions")
    ? JSON.parse(localStorage.getItem("hiddenTransactions") || "[]")
    : [];

  const [hiddenTxHashes, setHiddenTxHashes] = useState(hiddenTransactions);
  const [isOpen, setIsOpen] = useState(false);

  const swapkit = useSwapKit();
  const walletAddress = swapkit.client?.getAddress(Chain.THORChain);
  const {
    data: transactions,
    isLoading,
    refetch,
  } = api.listings.txHistoryByAddress.useQuery(
    { address: walletAddress },
    {
      enabled: !!walletAddress,
    },
  );

  const filteredTransactions = useMemo(() => {
    return (
      transactions?.filter(({ txHash }: ForgeHistoryLog) => !hiddenTxHashes.includes(txHash)) || []
    );
  }, [transactions, hiddenTxHashes]);

  const badge = useMemo(
    () => (filteredTransactions?.length > 9 ? "+9" : filteredTransactions?.length?.toString()),
    [filteredTransactions],
  );

  const hideAllTxHashes = useCallback(() => {
    const newHashes = transactions?.map((transaction) => transaction?.txHash) || [];

    setHiddenTxHashes((hashes) => {
      const hiddenHashesSet = new Set([...hashes, ...newHashes]);
      const hiddenHashes = Array.from(hiddenHashesSet);

      localStorage.setItem("hiddenTransactions", JSON.stringify(hiddenHashes));

      return hiddenHashes;
    });
  }, [transactions]);

  const clearHiddenTxHashes = useCallback(() => {
    const currentWalletHashes = transactions?.map((transaction) => transaction?.txHash) || [];

    setHiddenTxHashes((hashes) => {
      const hiddenHashesSet = new Set([...hashes]);

      currentWalletHashes?.forEach((hash) => {
        hiddenHashesSet.delete(hash);
      });

      const hiddenHashes = Array.from(hiddenHashesSet);

      localStorage.setItem("hiddenTransactions", JSON.stringify(hiddenHashes));

      return hiddenHashes;
    });
  }, [transactions]);

  const hideTxHash = useCallback((historyLog: ForgeHistoryLog) => {
    if (!historyLog?.txHash) return;

    setHiddenTxHashes((hashes) => {
      const hiddenHashesSet = new Set([...hashes, historyLog?.txHash]);
      const hiddenHashes = Array.from(hiddenHashesSet);

      localStorage.setItem("hiddenTransactions", JSON.stringify(hiddenHashes));

      return hiddenHashes;
    });
  }, []);

  useEffect(() => {
    if (isOpen && !isLoading) {
      void refetch();
    }
  }, [isLoading, isOpen, refetch]);

  const areTransactionsEmpty = filteredTransactions?.length <= 0;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger>
        <div className="relative flex h-6 w-6 shrink-0 items-center">
          <ListTodo className="h-auto w-full fill-white" />

          {badge && (
            <div className="absolute -right-1 -top-1 flex min-h-3 min-w-3 items-center justify-center bg-orange-gradient px-0.5 py-px text-[10px] font-medium leading-none text-black">
              {badge}
            </div>
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="flex max-h-[min(520px,var(--radix-popover-content-available-height))] w-[100vw] md:max-w-[480px]"
        side="bottom"
        sideOffset={26}
        align="end"
        alignOffset={-40}
      >
        <PopoverClose />

        <div className="flex w-full flex-col p-2 md:p-5">
          <div className="mb-1 mt-4 flex items-center border-b border-[#2D2226] pb-2 md:mt-3">
            <div className="text-lg font-medium uppercase">Transactions History</div>

            <Button size="sm" className="ml-auto" variant="ghost" onClick={hideAllTxHashes}>
              Hide All
            </Button>

            <Button size="sm" className="ml-2" variant="ghost" onClick={clearHiddenTxHashes}>
              Show All
            </Button>
          </div>

          <div className="-mr-3 overflow-y-auto overflow-x-hidden pr-3">
            <AnimatePresence mode="popLayout">
              {areTransactionsEmpty ? (
                <div
                  key="transaction-history-row-empty"
                  className="py-6 text-center text-base font-medium"
                >
                  You don&apos;t have any transactions in your history
                </div>
              ) : (
                <LayoutGroup>
                  {filteredTransactions?.map((historyLog: ForgeHistoryLog) => (
                    <TransactionHistoryRow
                      key={historyLog?.id}
                      historyLog={historyLog}
                      onHide={hideTxHash}
                    />
                  ))}
                </LayoutGroup>
              )}
            </AnimatePresence>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
